import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../api/client'
import type { Overview as OverviewData } from '../../api/types'

const usd = (n: number) => n.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 })

export default function Overview() {
  const [data, setData] = useState<OverviewData | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [retry, setRetry] = useState(0)

  useEffect(() => {
    let live = true
    let timer: ReturnType<typeof setTimeout> | undefined
    const controller = new AbortController()
    const refresh = async () => {
      try {
        const result = await api.overview(controller.signal)
        if (live) {
          setData(result)
          setError(null)
          timer = setTimeout(() => { void refresh() }, 10000)
        }
      } catch (e) {
        if (live) setError(e instanceof Error ? e.message : 'Unable to reach the server. Please try again.')
      }
    }
    void refresh()
    return () => { live = false; clearTimeout(timer); controller.abort() }
  }, [retry])

  if (error) return <div className="notice crit" role="alert"><p>Could not load overview: {error}</p><button className="btn secondary" onClick={() => { setError(null); setRetry(value => value + 1) }}>Retry</button></div>
  if (!data) return <p className="muted">Loading…</p>

  const f = data.funnel
  const responded = f.responded
  const steps: [string, number][] = [
    ['Companies', f.total],
    ['Contact found', f.contact_found],
    ['Contacted', f.contacted],
    ['Responded', responded],
    ['Spoken to', f.spoken_to],
    ['Screened', f.screened],
    ['Demos scheduled', f.demos_scheduled],
    ['Demos held', f.demos_held],
  ]

  return (
    <>
      <div className="app-head">
        <h1>Overview</h1>
        <span className="muted" style={{ fontSize: '0.85rem' }}>
          {data.engagements.filter((e) => e.live).length} of {data.engagements.length} engagements active
        </span>
      </div>

      <div className="stats" aria-label="Live counters">
        <div className="stat">
          <span className="eyebrow">Emails today</span>
          <strong>{data.live.emails_today}</strong>
        </div>
        <div className="stat">
          <span className="eyebrow">Calls in progress</span>
          <strong>{data.live.calls_in_progress}</strong>
        </div>
        <div className="stat">
          <span className="eyebrow">Browser jobs</span>
          <strong>{data.live.browser_jobs_running}</strong>
          <span className="sub">running</span>
        </div>
        <div className="stat">
          <span className="eyebrow">Spend to date</span>
          <strong>{usd(data.live.spend_to_date_usd)}</strong>
          <span className="sub">Committed {usd(data.live.committed_usd)} · cap {usd(data.live.spend_cap_usd)}</span>
          <span className="sub">Remaining {usd(data.live.remaining_usd)} · <Link to="/app/spend">View spend</Link></span>
        </div>
        <div className="stat">
          <span className="eyebrow">Needs review</span>
          <strong>{f.needs_review}</strong>
        </div>
        <div className="stat">
          <span className="eyebrow">Unreachable</span>
          <strong>{f.unreachable}</strong>
        </div>
        <div className="stat">
          <span className="eyebrow">Opted out</span>
          <strong>{f.opted_out}</strong>
        </div>
      </div>

      <section className="panel upcoming-meetings" style={{marginBottom:'1.5rem'}}>
        <div className="app-head"><h2>Upcoming meetings</h2><Link className="btn small secondary" to="/app/demos">Open calendar</Link></div>
        <p className="muted">Next 10 meetings · times shown in {Intl.DateTimeFormat().resolvedOptions().timeZone}.</p>
        {!data.upcoming_meetings ? <p className="muted">Meeting list is temporarily unavailable.</p> : !data.upcoming_meetings.length ? <p className="muted">No upcoming meetings. You can reserve an available time from a company's page.</p> : <div className="table-wrap"><table className="data"><thead><tr><th>When</th><th>Company</th><th>Analyst</th><th>Status</th></tr></thead><tbody>
          {data.upcoming_meetings.map(meeting=><tr key={meeting.id}><td>{new Date(meeting.start_at).toLocaleString()}</td><td><Link to={`/app/companies/${encodeURIComponent(meeting.company_id)}`}>{meeting.company_name}</Link></td><td>{meeting.analyst_name}</td><td><span className="pill">{meeting.status.replace(/_/g,' ')}</span>{meeting.invitation_status && <p className="muted">Invitation {meeting.invitation_status.replace(/_/g,' ')}</p>}{meeting.error && <p className="notice crit">{meeting.error}</p>}</td></tr>)}
        </tbody></table></div>}
      </section>
      <div className="grid-2">
        <section className="panel">
          <h2>Funnel</h2>
          <ol className="funnel">
            {steps.map(([label, n]) => (
              <li key={label}>
                <span>{label === 'Responded' ? <Link to="/app/companies?responded_channel=any">{label}</Link> : label}</span>
                <span className="bar">
                  <i style={{ width: `${Math.max(0, (n / Math.max(1,f.total)) * 100)}%` }} />
                </span>
                <span className="n">{n.toLocaleString()}</span>
              </li>
            ))}
          </ol>
          <p className="muted" style={{ fontSize: '0.8rem', marginTop: '0.75rem' }}>
            Responded by channel: <Link to="/app/companies?responded_channel=email">{f.responded_email} email</Link>, <Link to="/app/companies?responded_channel=call">{f.responded_call} call</Link>. A company using both channels counts once in the total.
          </p>
        </section>

        <section className="panel">
          <h2>Preparation readiness</h2>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>State</th>
                  <th style={{ textAlign: 'right' }}>Companies</th>
                </tr>
              </thead>
              <tbody>
                {(Object.entries(data.readiness) as [string, number][]).map(([k, v]) => (
                  <tr key={k}>
                    <td>
                      <span className={`pill${k === 'package_ready' ? ' ok' : k === 'needs_review' ? ' warn' : ''}`}>
                        {k.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>{v.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h2 style={{ marginTop: '1.5rem' }}>Engagements</h2>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Engagement</th>
                  <th>Outbound</th>
                </tr>
              </thead>
              <tbody>
                {data.engagements.map((e) => (
                  <tr key={e.engagement_id}>
                    <td className="wrap">{e.name}</td>
                    <td>
                      <span className={`pill ${e.live ? 'ok' : 'warn'}`}>{e.live ? 'on' : 'paused'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  )
}
